import React from "react";
import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";

function SelenseLanding({ onGetStarted, onSeeHowItWorks }) {
  return (
    <Box bg="white" color="gray.800">
      {/* Hero */}
      <Box
        position="relative"
        minH={{ base: "620px", md: "720px" }}
        display="flex"
        alignItems="center"
        overflow="hidden"
        bg="gray.900"
      >
        <Box
          position="absolute"
          inset={0}
          bgImage="url('/selense1.1.png')"
          bgSize="cover"
          bgPosition="center"
        />

        <Box
          position="absolute"
          inset={0}
          bgGradient={{
            base: "linear(to-r, blackAlpha.800, blackAlpha.500)",
            md: "linear(to-r, blackAlpha.900 0%, blackAlpha.700 45%, blackAlpha.200 100%)",
          }}
        />

        <Container
          maxW="1200px"
          position="relative"
          zIndex={1}
          py={{ base: 16, md: 24 }}
        >
          <Stack
            maxW={{ base: "100%", md: "680px" }}
            spacing={6}
          >
            <Text
              fontSize="sm"
              fontWeight="600"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color="teal.200"
            >
              A health memory for the people you love.
            </Text>

            <Heading
              as="h1"
              fontSize={{
                base: "3xl",
                sm: "4xl",
                md: "5xl",
                lg: "6xl",
              }}
              lineHeight="1.05"
              letterSpacing="-0.03em"
              color="white"
            >
              Your family&apos;s health story shouldn&apos;t live in
              WhatsApp, screenshots, hospital cards and someone&apos;s
              memory.
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="whiteAlpha.900"
              maxW="620px"
            >
              Selense helps you keep the important pieces together —
              what you&apos;ve noticed, what happened, and what matters
              next — so you can pick up where you left off.
            </Text>

            <HStack
              spacing={3}
              pt={2}
              flexWrap="wrap"
            >
              <Button
                size="lg"
                colorScheme="teal"
                onClick={onGetStarted}
              >
                Start with one person you love
              </Button>

              <Button
                size="lg"
                variant="outline"
                color="white"
                borderColor="whiteAlpha.600"
                _hover={{
                  bg: "whiteAlpha.200",
                }}
                onClick={onSeeHowItWorks}
              >
                See how Selense works
              </Button>
            </HStack>
          </Stack>
        </Container>
      </Box>

      {/* Introduction */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="1100px">
          <VStack
            spacing={5}
            textAlign="center"
            maxW="780px"
            mx="auto"
          >
            <Text
              fontSize="sm"
              fontWeight="600"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color="teal.600"
            >
              Meet Selense
            </Text>

            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "4xl" }}
              lineHeight="1.15"
              letterSpacing="-0.02em"
            >
              A calmer way to keep the health story of someone you love
              together.
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              lineHeight="1.8"
            >
              Selense gives families a place to remember important
              observations, health readings, medications and events —
              with the context that connects them.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Demo video */}
      <Box
        id="selense-demo"
        bg="gray.50"
        py={{ base: 16, md: 20 }}
      >
        <Container maxW="1100px">
          <VStack spacing={8}>
            <VStack
              spacing={3}
              textAlign="center"
            >
              <Text
                fontSize="sm"
                fontWeight="600"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="teal.600"
              >
                See Selense in action
              </Text>

              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "4xl" }}
              >
                Your loved one&apos;s story, kept together.
              </Heading>
            </VStack>

            <Box
              w="100%"
              aspectRatio={16 / 9}
              borderRadius="2xl"
              overflow="hidden"
              position="relative"
              bg="gray.900"
              boxShadow="xl"
              bgImage="url('/selense5.1.png')"
              bgSize="cover"
              bgPosition="center"
            >
              <Box
                position="absolute"
                inset={0}
                bg="blackAlpha.500"
              />

              <Button
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                borderRadius="full"
                w="72px"
                h="72px"
                fontSize="2xl"
                colorScheme="teal"
                aria-label="Play Selense demo"
                onClick={onSeeHowItWorks}
              >
                ▶
              </Button>
            </Box>

            <Text
              fontSize="sm"
              color="gray.500"
              textAlign="center"
            >
              Product demo coming soon.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Three value pillars */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="1100px">
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={6}
          >
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="2xl"
              p={{ base: 6, md: 8 }}
            >
              <Text
                fontSize="sm"
                fontWeight="600"
                color="teal.600"
                mb={3}
              >
                01
              </Text>

              <Heading as="h3" size="lg" mb={3}>
                Remember
              </Heading>

              <Text
                color="gray.600"
                lineHeight="1.7"
              >
                Keep important observations, events and health details
                together instead of relying on memory or scattered
                messages.
              </Text>
            </Box>

            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="2xl"
              p={{ base: 6, md: 8 }}
            >
              <Text
                fontSize="sm"
                fontWeight="600"
                color="teal.600"
                mb={3}
              >
                02
              </Text>

              <Heading as="h3" size="lg" mb={3}>
                Understand
              </Heading>

              <Text
                color="gray.600"
                lineHeight="1.7"
              >
                See what has been happening over time and keep the
                context around it — without turning your health story
                into a diagnosis.
              </Text>
            </Box>

            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="2xl"
              p={{ base: 6, md: 8 }}
            >
              <Text
                fontSize="sm"
                fontWeight="600"
                color="teal.600"
                mb={3}
              >
                03
              </Text>

              <Heading as="h3" size="lg" mb={3}>
                Prepare
              </Heading>

              <Text
                color="gray.600"
                lineHeight="1.7"
              >
                Bring a clearer record to conversations with a doctor
                or other healthcare professional.
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Doctor preparation */}
      <Box bg="teal.50" py={{ base: 16, md: 24 }}>
        <Container maxW="1100px">
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={{ base: 10, md: 16 }}
            align="center"
          >
            <Box
              flex="1"
              w="100%"
              aspectRatio={4 / 3}
              borderRadius="2xl"
              overflow="hidden"
              bgImage="url('/selense2.png')"
              bgSize="cover"
              bgPosition="center"
              boxShadow="lg"
            />

            <VStack
              flex="1"
              align="start"
              spacing={5}
            >
              <Text
                fontSize="sm"
                fontWeight="600"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="teal.700"
              >
                Before the next appointment
              </Text>

              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
              >
                Go into the next appointment with the story already
                together.
              </Heading>

              <Text
                color="gray.700"
                lineHeight="1.8"
              >
                After a hospital visit or a change you&apos;ve noticed,
                Selense helps you keep the important updates in one
                place and prepare a concise record you can share with
                your doctor.
              </Text>

              <Text
                color="gray.700"
                lineHeight="1.8"
              >
                That means less time reconstructing the story, and more
                time understanding what your loved one needs.
              </Text>

              <Text
                fontSize="sm"
                color="teal.800"
                fontWeight="500"
              >
                Selense organizes what you record. Your healthcare
                professional makes clinical decisions.
              </Text>
            </VStack>
          </Stack>
        </Container>
      </Box>

      {/* Trust */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="900px">
          <VStack
            spacing={5}
            textAlign="center"
          >
            <Text
              fontSize="sm"
              fontWeight="600"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color="teal.600"
            >
              Built around trust
            </Text>

            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "4xl" }}
            >
              Your health story, with you in control.
            </Heading>

            <Text
              maxW="720px"
              color="gray.600"
              lineHeight="1.8"
            >
              Selense is designed to help you remember and organize
              what you record. It does not diagnose conditions,
              prescribe medicines, or make medication changes.
            </Text>

            <SimpleGrid
              columns={{ base: 1, sm: 3 }}
              spacing={4}
              w="100%"
              pt={4}
            >
              <Box
                bg="gray.50"
                borderRadius="xl"
                p={5}
              >
                <Text fontWeight="600">
                  Your memory
                </Text>
                <Text
                  fontSize="sm"
                  color="gray.600"
                  mt={2}
                >
                  You decide what matters enough to remember.
                </Text>
              </Box>

              <Box
                bg="gray.50"
                borderRadius="xl"
                p={5}
              >
                <Text fontWeight="600">
                  Clear boundaries
                </Text>
                <Text
                  fontSize="sm"
                  color="gray.600"
                  mt={2}
                >
                  Selense does not replace your healthcare professional.
                </Text>
              </Box>

              <Box
                bg="gray.50"
                borderRadius="xl"
                p={5}
              >
                <Text fontWeight="600">
                  Continuity
                </Text>
                <Text
                  fontSize="sm"
                  color="gray.600"
                  mt={2}
                >
                  Pick up the health story where you left off.
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box
        bg="gray.900"
        py={{ base: 16, md: 24 }}
      >
        <Container maxW="800px">
          <VStack
            spacing={6}
            textAlign="center"
          >
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "5xl" }}
              color="white"
              lineHeight="1.1"
            >
              Start with one person you love.
            </Heading>

            <Text
              color="whiteAlpha.800"
              fontSize={{ base: "md", md: "lg" }}
            >
              Capture less. Understand more. Remember what matters.
            </Text>

            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button
                size="lg"
                colorScheme="teal"
                onClick={onGetStarted}
              >
                Start with Selense
              </Button>

              <Link
                href="/privacy-policy"
                color="whiteAlpha.900"
                fontWeight="600"
                _hover={{ textDecoration: "none", color: "teal.200" }}
              >
                Privacy Policy
              </Link>
              <Link
                href="/privacy-policy#faq"
                color="whiteAlpha.900"
                fontWeight="600"
                _hover={{ textDecoration: "none", color: "teal.200" }}
              >
                FAQs
              </Link>
            </HStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}

export default SelenseLanding;